<template>
  <div class="wrapper">
    <main id="app">
      <h1 class="site-title"><abbr title="accessibility">A11y</abbr> Day Leaderboard &mdash; January 24th, 2025!</h1>

      <div v-if="$apollo.loading" class="loading"></div>
      <div class="empty card" v-else-if="items.length < 1">
        <h2>Nothing yet</h2>
        <p>
          There are no contributions yet.
          <a href="https://gitlab.com/gitlab-org/gitlab-services/design.gitlab.com/-/issues/1978">Kick us off</a>.
        </p>
      </div>
      <div class="loaded" v-else>
        <div class="card">
          <MigrationPodium :users="podiumUsers"></MigrationPodium>
        </div>
        <div class="card">
          <GlTableLite :items="items" :fields="fields"></GlTableLite>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { GlTableLite } from "@gitlab/ui";
import migrationMergeRequestQuery from "@/graphql/mergeRequests.js";
import MigrationPodium from "@/components/MigrationPodium.vue";
import getDiffFor from "@/helpers/getDiffFor";
import calculateMrScoreFor from "@/helpers/calculateMrScoreFor";

export default {
  name: "App",
  components: {
    GlTableLite,
    MigrationPodium,
  },
  data: () => ({
    fields: [
      { key: "position", label: "#" },
      { key: "name", label: "Name", tdClass: "name-data" },
      { key: "score", label: "Score" },
    ],
  }),
  apollo: {
    mergeRequests: {
      query: migrationMergeRequestQuery,
      update(data) {
        const mrs = data?.project?.mergeRequests?.nodes;
        if (
          mrs &&
          this.mergeRequests &&
          mrs.length > this.mergeRequests.length
        ) {
          getDiffFor(mrs, this.mergeRequests).forEach(this.notify);
        }
        return mrs;
      },
      pollInterval: 60 * 1000,
    },
  },
  computed: {
    podiumUsers() {
      return this.users.slice(0, 3);
    },
    items() {
      return this.users.map(({ name, score, mrs }, i) => ({
        name: mrs.length >= 3 ? `${name}` : name,
        score,
        position: i + 1,
      }));
    },
    userMap() {
      const users = {};
      const updateUser = (user, mr, isApprover) => {
        const existingUser = users[user.id];
        const mrScore = calculateMrScoreFor(mr);
        const userScore = isApprover ? Math.floor(mrScore * 0.5) : mrScore;

        if (existingUser) {
          if (!isApprover) {
            existingUser.mrs.push(mr);
          }
          existingUser.score += userScore;
        } else {
          users[user.id] = {
            ...user,
            score: userScore,
            mrs: isApprover ? [] : [mr],
          };
        }
      };

      this.mergeRequests.forEach((mr) => {
        const { author } = mr;

        updateUser(author, mr);

        const approvers = mr.approvedBy.nodes;
        if (approvers.length) {
          approvers.forEach((approver) => updateUser(approver, mr, true));
        }
      });
      return users;
    },
    users() {
      return Object.values(this.userMap).sort((a, b) =>
        a.score > b.score ? -1 : 1
      );
    },
  },
  methods: {
    notify(mr) {
      const { author, state } = mr;
      const { name } = author;

      this.$toast.show(`${name} just ${state} an MR! 🎉`);

      if (this.userMap[author.id].mrs.length === 2) {
        setTimeout(() => {
          this.$toast.show(
            `${name} opened their third MR, they're eating for free!`
          );
        }, 2000);
      }
    },
  },
};
</script>

<style>
@import "@gitlab/ui/dist/index.css";
@import "./assets/main.css";

.wrapper {
  width: 100vw;
  min-height: 100vh;
  max-width: 100%;
}

.loaded {
  display: grid;
  gap: 1em;
}

.toggle {
  position: absolute;
  top: 1.6rem;
  right: 1.6rem;
  z-index: 99;
}
.gl-toggle-wrapper {
  line-height: 1.6em;
  gap: 0.6em;
}

.site-title {
  margin: 0.3rem 0 1rem;
  text-align: center;
}
</style>
